import { useParams, Link, Navigate } from "react-router-dom";
import SEO from "@/components/SEO";
import { getCity, cities } from "@/data/cities";
import { services } from "@/data/services";
import { RevealBlock } from "@/components/Reveal";
import { TID } from "@/lib/testIds";

const DEFAULT_KEYWORDS = (name) => [
  `security guards ${name}`,
  `${name} security company`,
  `${name} mobile patrol`,
  `${name} event security`,
  `${name} construction security`,
  `${name} loss prevention`,
  `24/7 security ${name} Nova Scotia`,
  `licensed security ${name} NS`,
  `${name} security services Canada`,
  `${name} security company Canada`,
  `${name} 24 hour security`,
  `${name} corporate security`,
  `${name} retail security`,
  `${name} residential security`,
  `${name} concierge security`,
  `${name} fire watch services`,
  `${name} alarm response`,
  `Atlantic Canada security ${name}`,
  `${name} Nova Scotia security guards`,
  `Canadian security company ${name}`,
  `licensed security guard ${name} Canada`,
  `${name} industrial security`,
  `${name} executive protection`,
  `${name} close protection Canada`,
];

const generalBody = (name, region) => [
  `Our ${name} operation is staffed by officers who live in and around ${region}. Local roster, local knowledge, local response times. Every officer assigned to a ${name} post carries a Nova Scotia Security Guard licence, a current Standard First Aid + CPR-C certificate, and completes site-specific orientation before their first shift.`,
  `${name}'s risk profile is shaped by its geography, its economy, and its rhythms. We build patrol beats, gatehouse rosters, and event plans around what we observe on the ground — not around a generic playbook. Whether your site is a retail storefront, a construction lay-down yard, an industrial facility, an office tower, or a private residence, our team is briefed on the neighbourhood, the routes, and the operational context.`,
  `From uniformed presence to plain-clothes loss prevention, from mobile patrols during shoulder hours to full 24/7 dedicated posts, we scale to the assignment. Every engagement includes written daily activity reports, timestamped patrol logs, and monthly incident analytics.`,
  `${name} is served by our regional supervisor, who is on call around the clock for our clients here. We audit every incident file, run quarterly client reviews, and adjust our roster to match seasonal patterns — including the tourist season, storm season, and any special events in the community.`,
  `Common ${name} assignments include construction site security, retail loss prevention, mobile patrol beats, event security, corporate lobby posts, and residential estate protection. Every category is delivered by a specialised sub-team — construction officers understand WHMIS and fall-arrest awareness, retail officers understand Powers of Arrest and evidence handling, event officers understand crowd flow and ingress mapping.`,
  `Nova Scotia weather is a real variable for ${name} operations. Winter nor'easters, ice storms, spring thaw, and Atlantic hurricane-season remnants each change the risk landscape. Our winter rosters include storm reserve staff, our summer festival rosters include heat-management protocols, and our shoulder-season patrols cover exactly the hours when most losses occur — dusk to dawn, September through March.`,
  `Because we do not display a phone number or an email, every ${name} engagement begins with a written brief. This discipline keeps the record clean, keeps our supervisors accountable, and gives every client a paper trail from the first conversation to the last shift. Written briefs are received by post at our Halifax office, or by letter of intent through your legal counsel.`,
  `We are a licensed Nova Scotia security company — bonded to $5M commercial general liability, in good standing with the Workers' Compensation Board, and audited annually. Every officer we assign to a ${name} post has been personally onboarded by a licensed supervisor.`,
];

export default function CityDetail() {
  const { slug } = useParams();
  const city = getCity(slug);
  if (!city) return <Navigate to="/cities" replace />;

  const body = city.body || generalBody(city.name, city.region);
  const intro =
    city.intro ||
    `${city.name}, in ${city.region}, is one of the ${cities.length} Nova Scotia communities we serve continuously. Our officers know its streets, its industries, and its rhythms — and post accordingly.`;

  return (
    <>
      <SEO
        title={`Security Guard Services in ${city.name}, Nova Scotia`}
        description={intro.slice(0, 180)}
        path={`/cities/${city.slug}`}
        keywords={[...(city.keywords || []), ...DEFAULT_KEYWORDS(city.name)]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SecurityService",
          name: `Nova Scotia Security Services — ${city.name}`,
          areaServed: {
            "@type": "City",
            name: city.name,
            containedInPlace: { "@type": "AdministrativeArea", name: "Nova Scotia" },
          },
        }}
      />

      <section data-testid={`${TID.cities.detail}-${city.slug}`} className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-16 md:pt-20">
        <Link
          to="/cities"
          className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#111111]/60 hover:text-[#C17A43]"
        >
          ← All Cities
        </Link>

        <div className="grid grid-cols-12 gap-4 mt-12 md:mt-16 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] mb-6">
              — {city.region} · {city.population || "Population served"}
            </p>
            <RevealBlock>
              <h1 className="font-serif text-[clamp(3rem,10vw,10rem)] leading-[0.86] tracking-tighter text-[#111111]">
                {city.name}
              </h1>
            </RevealBlock>
            {city.tagline && (
              <p className="mt-8 font-serif italic text-2xl md:text-3xl text-[#184C59] max-w-[36ch] leading-tight">
                {city.tagline}
              </p>
            )}
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pb-6 border border-[#D9D6D0] p-8 bg-[#F5F3EF]">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C17A43] mb-4">
              — At a Glance
            </p>
            <dl className="space-y-3">
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#111111]/50">Region</dt>
                <dd className="font-serif text-lg text-[#111111]">{city.region}</dd>
              </div>
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#111111]/50">Population served</dt>
                <dd className="font-serif text-lg text-[#111111]">{city.population || "—"}</dd>
              </div>
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#111111]/50">Coverage</dt>
                <dd className="font-serif text-lg text-[#111111]">24 / 7 / 365</dd>
              </div>
            </dl>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-8 mt-20 md:mt-28 pb-24">
          <div className="col-span-12 lg:col-span-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] sticky top-32">
              — Local Notes
            </p>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <RevealBlock>
              <p className="font-serif italic text-3xl md:text-5xl leading-tight text-[#111111] mb-12">
                {intro}
              </p>
            </RevealBlock>
            {body.map((p, i) => (
              <RevealBlock key={i} delay={i * 0.08}>
                <p className="font-sans text-base md:text-lg leading-relaxed text-[#111111]/80 mb-6">
                  {p}
                </p>
              </RevealBlock>
            ))}

            {/* Keyword coverage */}
            <div className="mt-16 border-t border-[#D9D6D0] pt-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#111111]/50 mb-4">
                — Commonly searched · {city.name}
              </p>
              <div className="flex flex-wrap gap-2">
                {[...new Set([...(city.keywords || []), ...DEFAULT_KEYWORDS(city.name)])].map((k) => (
                  <span
                    key={k}
                    className="font-mono text-[10px] uppercase tracking-[0.18em] border border-[#D9D6D0] px-3 py-1.5 text-[#111111]/70"
                  >
                    {k}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Services offered in this city - links to combo pages */}
        <div className="border-t border-[#D9D6D0] py-16 md:py-24">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] mb-4">
            — 15 disciplines delivered in {city.name}
          </p>
          <p className="font-sans text-sm md:text-base text-[#111111]/70 max-w-[64ch] mb-10 leading-relaxed">
            Each discipline has its own dedicated {city.name} landing page — with local risk profiles, deliverables tuned to the community, and route intelligence.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D9D6D0] border border-[#D9D6D0]">
            {services.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}/${city.slug}`}
                className="group bg-[#F5F3EF] p-6 md:p-8 hover:bg-[#111111] hover:text-[#F5F3EF] transition-colors duration-500 flex justify-between items-start min-h-[140px]"
              >
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] opacity-60">{s.number}</p>
                  <h3 className="font-serif text-xl md:text-2xl mt-4 group-hover:italic transition-[font-style] duration-300">
                    {s.name} · {city.name}
                  </h3>
                </div>
                <span className="text-2xl opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-transform">
                  →
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Nearby cities */}
        <div className="border-t border-[#D9D6D0] py-16">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] mb-6">
            — Nearby communities
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {cities.filter((c) => c.slug !== city.slug).slice(0, 30).map((c) => (
              <Link
                key={c.slug}
                to={`/cities/${c.slug}`}
                className="font-serif italic text-lg md:text-xl text-[#111111]/70 hover:text-[#C17A43]"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
